import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useId
} from "react";

// clsx simples
function clsx(...a: (string | false | null | undefined)[]) {
  return a.filter(Boolean).join(" ");
}

export interface ToggleI18nContext {
  getDefaultErrorMessage: () => string;
  getDefaultSuccessMessage?: () => string;
}

export function resolveMessage(
  value: boolean,
  msg: string | ((value: boolean) => string) | undefined,
  fallback: () => string
): string {
  if (typeof msg === "function") {
    try { return msg(value); } catch { return fallback(); }
  }
  if (typeof msg === "string") return msg;
  return fallback();
}

export interface ToggleProps {
  id?: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  theme?: "light" | "dark";
  error?: boolean;
  errorMessage?: string | ((value: boolean) => string);
  successMessage?: string | ((value: boolean) => string);
  helperText?: string;
  tooltip?: string;
  className?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement> & { "data-testid"?: string };
  tabIndex?: number;
  autoFocus?: boolean;
  accessibilityLabel?: string;
  accessibilityErrorMessage?: string;
  i18nMessages?: ToggleI18nContext;
  "data-testid"?: string;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  onRightIconClick?: () => void;
  indeterminate?: boolean;
}

const Toggle = React.memo(forwardRef<HTMLInputElement, ToggleProps>((props, ref) => {
  const {
    id,
    value,
    onValueChange,
    label,
    disabled = false,
    readOnly = false,
    required = false,
    theme = "light",
    error = false,
    errorMessage,
    successMessage,
    helperText,
    tooltip,
    className,
    containerProps,
    tabIndex,
    autoFocus,
    accessibilityLabel,
    accessibilityErrorMessage,
    i18nMessages,
    "data-testid": dataTestId,
    leftIcon,
    rightIcon,
    onRightIconClick,
    indeterminate,
  } = props;

  const generatedId = useId();
  const internalId = id || generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);

  const [checked, setChecked] = useState(!!value);
  useEffect(() => { setChecked(!!value); }, [value]);

  const i18n = i18nMessages || {
    getDefaultErrorMessage: () => "Valor inválido",
    getDefaultSuccessMessage: () => "OK"
  };

  const toggle = useCallback(() => {
    if (disabled || readOnly) return;
    const next = !checked;
    setChecked(next);
    onValueChange(next);
  }, [disabled, readOnly, checked, onValueChange]);

  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      aria-required={required}
      aria-invalid={error}
      aria-label={
        error
          ? resolveMessage(
              checked,
              (accessibilityErrorMessage ?? errorMessage),
              i18n.getDefaultErrorMessage
            )
          : (accessibilityLabel || label || "Alternar")
      }
      tabIndex={tabIndex ?? 0}
      onClick={toggle}
      onKeyDown={e => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          toggle();
        }
      }}
      data-testid={dataTestId || containerProps?.["data-testid"] || "toggle"}
      className={clsx(
        "flex items-center gap-2 mb-4 relative outline-none flex-wrap min-w-0 w-full box-border",
        theme === "dark" ? "text-gray-100" : "text-gray-900",
        disabled && "opacity-60 cursor-default",
        className || containerProps?.className
      )}
      {...containerProps}
    >
      {leftIcon && (
        <span className="mr-2 flex items-center">{leftIcon}</span>
      )}
      {label && (
        <label
          htmlFor={internalId}
          className={clsx(
            "text-base mr-2 flex items-center gap-1 min-w-0 flex-1 leading-[1.3]",
            theme === "dark" ? "text-gray-300" : "text-gray-700"
          )}
        >
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      <div
        className={clsx(
          "relative rounded-full w-11 h-6 transition-colors duration-200 flex-shrink-0",
          disabled
            ? (theme === "dark" ? "bg-gray-700" : "bg-gray-300")
            : checked
            ? (theme === "dark" ? "bg-blue-500" : "bg-blue-600")
            : (theme === "dark" ? "bg-gray-600" : "bg-gray-300"),
          indeterminate && "border-2 border-yellow-400"
        )}
      >
        <input
          ref={inputRef}
          id={internalId}
          name={internalId}
          type="checkbox"
          checked={checked}
          readOnly
          style={{ display: "none" }}
          autoFocus={autoFocus}
        />
        <div
          className={clsx(
            "absolute top-1/2 left-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200 -translate-y-1/2",
            checked ? "left-5 scale-110" : "left-1 scale-100"
          )}
          style={indeterminate ? { background: "#facc15" } : undefined}
        >
          {indeterminate && (
            <span
              className="absolute inset-0 flex items-center justify-center text-yellow-700 text-xs font-bold"
              style={{ fontSize: 12 }}
            >
              &ndash;
            </span>
          )}
        </div>
      </div>
      {rightIcon && (
        <span
          className="ml-2 flex items-center cursor-pointer"
          onClick={e => {
            e.stopPropagation();
            if (onRightIconClick) onRightIconClick();
          }}
          tabIndex={onRightIconClick ? 0 : undefined}
          role={onRightIconClick ? "button" : undefined}
          aria-label={onRightIconClick ? "Abrir configurações" : undefined}
        >
          {rightIcon}
        </span>
      )}
      {helperText && !error && (
        <p className={clsx("text-sm ml-1", theme === "dark" ? "text-gray-400" : "text-gray-600")}>
          {helperText}
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600 ml-1">
          {resolveMessage(checked, errorMessage, i18n.getDefaultErrorMessage)}
        </p>
      )}
      {!error && successMessage && checked && (
        <p className="text-sm text-green-600 ml-1">
          {resolveMessage(
            checked,
            successMessage,
            i18n.getDefaultSuccessMessage || (() => "OK")
          )}
        </p>
      )}
      {tooltip && <span className="sr-only">{tooltip}</span>}
    </div>
  );
}));

Toggle.displayName = "Toggle";

export const ToggleMessagesContext = React.createContext<ToggleI18nContext>({
  getDefaultErrorMessage: () => "Valor inválido",
  getDefaultSuccessMessage: () => "OK"
});

export default Toggle;